import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, X } from 'lucide-react';
import { getLanguage, t } from '../../../i18n';
import { CARD_DRAFT_TYPES, getCardTemplate } from '../composer/cardTemplates';
import { validateComposerContent } from '../composer/composerValidation';
import { formatComposerError } from '../composer/composerMessages';
import CardDraftPreview from './CardDraftPreview';
import { cardPreview, cardToDraft } from '../../../components/noteEditorModel';
import { editorText } from '../../../components/noteEditorText';

const TYPE_OPTIONS = [
  CARD_DRAFT_TYPES.SIMPLE,
  CARD_DRAFT_TYPES.NORMAL,
  CARD_DRAFT_TYPES.ARTICLE,
  CARD_DRAFT_TYPES.DIALOG,
];

function CardBuilderDialog({ isOpen, onCancel, onAdd, initialCard = null, suspended = false, allowedTypes = TYPE_OPTIONS }) {
  const language = getLanguage();
  const [selectedType, setSelectedType] = useState(CARD_DRAFT_TYPES.SIMPLE);
  const [draft, setDraft] = useState(getCardTemplate(CARD_DRAFT_TYPES.SIMPLE));
  const [validation, setValidation] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen || suspended) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled)') || []).filter((element) => !element.closest('fieldset[disabled]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', keydown);
      previousFocus?.focus?.();
    };
  }, [isOpen, onCancel, suspended]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelectedType(initialCard ? cardPreview(initialCard).draftType : CARD_DRAFT_TYPES.SIMPLE);
    setDraft(initialCard ? cardToDraft(initialCard) : getCardTemplate(CARD_DRAFT_TYPES.SIMPLE));
    setValidation(null);
    return undefined;
  }, [isOpen, initialCard]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timeoutId = setTimeout(() => {
      setValidation(validateComposerContent(draft, {
        expectedType: selectedType,
        requireSingleCard: true,
      }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [draft, isOpen, selectedType]);

  const previewCard = useMemo(() => validation?.isValid ? validation.cards[0] : null, [validation]);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setDraft(getCardTemplate(type));
    setValidation(null);
  };

  const handleAdd = () => {
    const result = validateComposerContent(draft, {
      expectedType: selectedType,
      requireSingleCard: true,
    });
    setValidation(result);
    if (result.isValid) onAdd(draft);
  };

  if (!isOpen) return null;

  return (
    <div className="composer-modal-overlay" role="presentation" style={suspended ? { display: 'none' } : undefined}>
      <div ref={dialogRef} tabIndex={-1} className="composer-builder-dialog" role="dialog" aria-modal="true" aria-labelledby="card-builder-title">
        <div className="composer-modal-header">
          <div>
            <div className="composer-modal-kicker">{t('noteComposer.builder.kicker', language)}</div>
            <h2 id="card-builder-title">{initialCard ? editorText('editCard', language) : t('noteComposer.builder.title', language)}</h2>
          </div>
          <button type="button" className="composer-icon-button" onClick={onCancel} aria-label={t('common.close', language)}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <fieldset className="composer-type-selector" disabled={Boolean(initialCard)}>
          <legend>{t('noteComposer.builder.typeLegend', language)}</legend>
          {allowedTypes.map((type) => (
            <label key={type} className={selectedType === type ? 'selected' : ''}>
              <input
                type="radio"
                name="card-draft-type"
                value={type}
                checked={selectedType === type}
                onChange={() => handleTypeChange(type)}
              />
              <span>{t(`noteComposer.types.${type}`, language)}</span>
            </label>
          ))}
        </fieldset>

        <div className="composer-builder-workspace">
          <div className="composer-builder-editor">
            <label htmlFor="card-builder-text">{t('noteComposer.builder.formatLabel', language)}</label>
            <textarea
              id="card-builder-text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              spellCheck="false"
            />
          </div>
          <div className="composer-builder-preview">
            <div className="composer-preview-label">{t('noteComposer.builder.previewLabel', language)}</div>
            {previewCard ? (
              <CardDraftPreview card={previewCard} />
            ) : validation?.error ? (
              <div className="composer-preview-error" role="alert">
                <AlertCircle size={20} aria-hidden="true" />
                <span>{formatComposerError(validation.error, language)}</span>
              </div>
            ) : (
              <div className="composer-preview-waiting">{t('noteComposer.builder.checking', language)}</div>
            )}
          </div>
        </div>

        <div className="composer-modal-actions">
          <button type="button" className="composer-button secondary" onClick={onCancel}>{t('common.cancel', language)}</button>
          <button type="button" className="composer-button primary" onClick={handleAdd}>
            <Check size={17} aria-hidden="true" />
            {initialCard ? t('common.save', language) : t('noteComposer.builder.addCard', language)}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CardBuilderDialog;
